import { ArrayUtil } from "@coten-inc/utils";

/**
 * Splits a `linkGeoItems` cell the way the importer does, so the editor shows what the job will
 * actually read rather than a second opinion.
 *
 * The importer's `linkGeoItems` codec reads a cell in one of two forms. A cell starting with `[` is a
 * JSON array whose elements are bare IDs or attributed entries (`{ geoItemId, ... }`); a cell that
 * does not parse, or holds any other element, is rejected. Any other cell is an ID list: split on
 * commas, trimmed and lowercased, with a single trailing empty token dropped — `"<id>,"` is one ID,
 * while `"<id>,,<id>"` keeps the empty token and fails validation.
 *
 * Each GeoItem is listed once, since several attributed entries may link the same one.
 */

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

type ParsedGeoItemIdCell = { ids: string[]; invalid: string[] };

const readIdListTokens = (cell: string): string[] => {
  const tokens = cell.split(",").map((token) => token.trim().toLowerCase());
  return tokens.length > 1 && tokens[tokens.length - 1] === "" ? tokens.slice(0, -1) : tokens;
};

const readJsonArrayTokens = (cell: string): string[] | null => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(cell);
  } catch {
    return null;
  }
  if (!Array.isArray(parsed)) {
    return null;
  }
  return parsed.map((element: unknown) => {
    if (typeof element === "string") {
      return element.trim().toLowerCase();
    }
    if (element && typeof element === "object" && !Array.isArray(element)) {
      const { geoItemId } = element as { geoItemId?: unknown };
      return typeof geoItemId === "string" ? geoItemId.trim().toLowerCase() : "(invalid entry)";
    }
    return "(invalid entry)";
  });
};

export const parseGeoItemIdCell = (value: string): ParsedGeoItemIdCell => {
  const trimmed = value.trim();
  if (trimmed === "") {
    return { ids: [], invalid: [] };
  }

  const tokens = trimmed.startsWith("[") ? readJsonArrayTokens(trimmed) : readIdListTokens(trimmed);
  if (tokens === null) {
    return { ids: [], invalid: ["(invalid JSON)"] };
  }

  const ids = ArrayUtil.uniq(tokens.filter((token) => UUID.test(token)));
  const invalid = tokens.filter((token) => !UUID.test(token)).map((token) => (token === "" ? "(empty)" : token));
  return { ids, invalid };
};
